import * as React from 'react'

import {
  Avatar,
  Box,
  Button,
  Icon,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type JobRole from 'modules/job_catalog/store/models/JobRole'

import AnalyticsTableRow from '../Analytics/TableRow'

export interface Props {
  role: JobRole
  isEditable: boolean
  loadingAnalytics?: boolean
  canSeeAggregatedInfo?: boolean
  onRemove: () => void
}

const Row: React.FC<Props> = ({
  role,
  isEditable,
  loadingAnalytics,
  canSeeAggregatedInfo,
  onRemove,
}) => {
  const { id, name, supervisors_ids } = role.attributes
  const { navigation } = useNavigation()
  const [open, toggle] = useToggle(false)
  const [openName, toggleName] = useToggle(false)

  const handleRemove = () => {
    track(trackingEvents.jobCatalog.archiveJobRoleStart)
    onRemove()
  }

  const formattedLabel = name?.replace(/[\W_]+/g, ' ')

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box flexDirection="row" alignItems="center" gap="s24">
          <Avatar size="s40" label={formattedLabel} />
          <Box overflowX="hidden">
            <Tooltip.Alt
              attachment="bottom center"
              toggle={toggleName}
              open={openName}
              offset="0 -10px"
              label={name}
              textAlign="left"
            >
              <Text.Normal weight="normal" whiteSpace="nowrap" color="grey1000" ellipsis>
                {name}
              </Text.Normal>
            </Tooltip.Alt>
          </Box>

          {isEditable && !supervisors_ids.length && (
            <Tooltip.Alt
              attachment="bottom center"
              open={open}
              toggle={toggle}
              label={i18n.t('job_catalog.no_supervisors_assigned')}
            >
              <Icon.Small icon={'info'} encircled={'grey800'} />
            </Tooltip.Alt>
          )}
        </Box>
      </Table.Cell>
      <AnalyticsTableRow
        role={role}
        loading={loadingAnalytics}
        canSeeAggregatedInfo={canSeeAggregatedInfo}
      />
      <Table.Cell colSpan={1}>
        <Box justifyContent="flexEnd" flexDirection="row" gap="s8">
          {isEditable && (
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative
                  type="secondary"
                  aria-label={i18n.t('common.more_options')}
                  iconName="ellipsis"
                />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.RouterOption
                  title={i18n.t('job_catalog.edit_settings')}
                  to={{
                    pathname: navigation.employees.jobCatalog.role.settings.fullPath({
                      id: id.toString(),
                    }),
                  }}
                />
                <PopoverMenu.TouchableOption
                  title={i18n.t('job_catalog.remove_role')}
                  onClick={handleRemove}
                  danger
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          )}
          <Button.Router
            type="secondary"
            to={navigation.employees.jobCatalog.role.fullPath({ id: id.toString() })}
            onClick={() => {
              track(trackingEvents.jobCatalog.goToJobRoleFurtherInfoSubmit)
            }}
            disabled={role.isArchived}
            aria-label={i18n.t('job_catalog.see_job_role_details')}
            iconName="arrow-right"
            extra={{
              'data-intercom-target': 'job-catalog-roles-table-see-job-role-details-button',
            }}
          />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Row)
